import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../i18n';
import useEditorAutoGrow from './useEditorAutoGrow';

// Syntax: [option1/option2] anywhere in the sentence. Both options can be
// multi-word phrases. The first option (index 0) is only the default correct
// answer — the teacher picks per-choice in the config panel.
const CHOICE_REGEX = /\[([^\]]+?)\/([^\]]+?)\]/g;

export const parseSentence = (text) => {
  const segments = [];
  let lastIdx = 0;
  let choiceIdx = 0;
  const src = text || '';
  CHOICE_REGEX.lastIndex = 0;
  let match;
  while ((match = CHOICE_REGEX.exec(src)) !== null) {
    if (match.index > lastIdx) {
      segments.push({ type: 'text', text: src.slice(lastIdx, match.index) });
    }
    segments.push({
      type: 'choice',
      choiceIdx,
      options: [match[1].trim(), match[2].trim()],
    });
    choiceIdx += 1;
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < src.length) {
    segments.push({ type: 'text', text: src.slice(lastIdx) });
  }
  return { segments, choiceCount: choiceIdx };
};

const ChooseWordElement = ({
  element,
  isEditor = false,
  studentView = false,
  answers: externalAnswers = null,
  showResult = false,
  perChoiceResults = null,
  onAnswersChange,
  onCheck,
  disabled = false,
  hideCheckButton = false,
  onAutoGrow,
}) => {
  const { t } = useTranslation();
  const { content, style } = element;
  const {
    question = '',
    sentences = [],
    fontFamily = 'Inter',
    fontSize = 18,
    textColor = '#1f2937',
    buttonColor = '#2A4D25',
    highlightColor = '#2A4D25',
    selectedFillColor = '',
    selectedTextColor = '',
    actionBarColor = '#f9fafb',
  } = content || {};

  const backgroundColor = style?.backgroundColor || '#ffffff';
  const isActionBarTransparent = actionBarColor === 'transparent';

  const [answers, setAnswers] = useState(externalAnswers || {});
  useEffect(() => {
    if (externalAnswers) setAnswers(externalAnswers);
  }, [externalAnswers]);

  const scrollRef = useRef(null);
  useEditorAutoGrow({
    enabled: isEditor,
    scrollRef,
    currentHeightPct: element.size?.height || 0,
    contentCount: sentences.length,
    onAutoGrow,
  });

  const updateAnswers = (next) => {
    setAnswers(next);
    onAnswersChange?.(next);
  };

  const handlePick = (sentenceId, choiceIdx, optionIdx) => {
    if (isEditor || disabled || showResult) return;
    const prev = answers[sentenceId] || [];
    const next = { ...answers, [sentenceId]: [...prev] };
    next[sentenceId][choiceIdx] = optionIdx;
    updateAnswers(next);
  };

  const parsedSentences = sentences.map((s) => ({
    ...s,
    parsed: parseSentence(s.text || ''),
  }));

  const allFilled = parsedSentences.length > 0
    && parsedSentences.every((s) => {
      if (s.parsed.choiceCount === 0) return true;
      const arr = answers[s.id] || [];
      for (let i = 0; i < s.parsed.choiceCount; i += 1) {
        if (arr[i] !== 0 && arr[i] !== 1) return false;
      }
      return true;
    });

  const allCorrect = perChoiceResults
    && Object.keys(perChoiceResults).length > 0
    && Object.values(perChoiceResults).every((arr) => (arr || []).every((v) => v === true));

  const hideChrome = !isEditor;
  const handleCheck = () => onCheck?.(answers);

  const renderOption = (sentence, seg, optionIdx) => {
    const label = seg.options[optionIdx];
    const selectedIdx = (answers[sentence.id] || [])[seg.choiceIdx];
    const isSelected = selectedIdx === optionIdx;
    const correctIdx = (sentence.correctIndices || [])[seg.choiceIdx];

    // Editor view: show correct answer highlighted (teacher preview).
    const editorHighlight = isEditor && correctIdx === optionIdx;

    let borderColor = 'transparent';
    let bgColor = 'transparent';
    let color = textColor;
    let borderStyle = 'solid';

    if (editorHighlight) {
      borderColor = highlightColor;
    } else if (showResult) {
      const perResults = perChoiceResults?.[sentence.id] || [];
      const wasCorrect = perResults[seg.choiceIdx];
      if (isSelected) {
        if (wasCorrect) {
          borderColor = '#22c55e';
          bgColor = '#dcfce7';
          color = '#166534';
        } else {
          borderColor = '#ef4444';
          bgColor = '#fee2e2';
          color = '#991b1b';
        }
      } else {
        color = '#9ca3af';
      }
    } else if (isSelected) {
      borderColor = highlightColor;
      bgColor = selectedFillColor || `${highlightColor}14`; // default ~8% alpha
      if (selectedTextColor) color = selectedTextColor;
    }

    const clickable = !isEditor && !disabled && !showResult;

    return (
      <button
        type="button"
        key={`${sentence.id}_${seg.choiceIdx}_${optionIdx}`}
        onClick={() => handlePick(sentence.id, seg.choiceIdx, optionIdx)}
        disabled={!clickable}
        className={`inline-flex items-center rounded-full border-2 transition ${
          clickable ? 'cursor-pointer hover:opacity-80' : 'cursor-default'
        }`}
        style={{
          borderColor,
          backgroundColor: bgColor,
          color,
          borderStyle,
          padding: `${Math.max(1, Math.round(fontSize * 0.05))}px ${Math.round(fontSize * 0.5)}px`,
          fontFamily,
          fontSize: `${fontSize}px`,
          lineHeight: 1.2,
          fontWeight: isSelected || editorHighlight ? 600 : 400,
          verticalAlign: 'baseline',
        }}
      >
        {label || ' '}
      </button>
    );
  };

  return (
    <div
      className={`w-full h-full rounded-xl overflow-hidden flex flex-col ${
        hideChrome ? '' : 'border-2 shadow-sm'
      }`}
      style={{
        backgroundColor: hideChrome ? 'transparent' : backgroundColor,
        borderColor: showResult && !isEditor
          ? (allCorrect ? '#22c55e' : '#ef4444')
          : '#e5e7eb',
      }}
    >
      <div ref={scrollRef} className="flex-1 min-h-0 min-w-0 p-5 overflow-y-auto overflow-x-hidden">
        {question && (
          <p
            className="font-medium mb-3"
            style={{
              fontSize: `${fontSize}px`,
              fontFamily,
              color: textColor,
              lineHeight: 1.3,
            }}
          >
            {question}
          </p>
        )}

        {parsedSentences.length === 0 ? (
          <p className="text-sm text-gray-400">
            {t('games.chooseWord.addSentenceHint')} <code>I [go/goes] to school every day.</code>
          </p>
        ) : (
          <div className="space-y-3">
            {parsedSentences.map((s, idx) => (
              <div
                key={s.id}
                className="flex items-baseline gap-2"
                style={{
                  fontSize: `${fontSize}px`,
                  fontFamily,
                  color: textColor,
                  lineHeight: 1.7,
                }}
              >
                <span
                  className="flex-none text-gray-400 font-medium"
                  style={{ fontSize: `${Math.round(fontSize * 0.85)}px` }}
                >
                  {idx + 1}.
                </span>
                <p className="flex-1 min-w-0" style={{ margin: 0, overflowWrap: 'break-word' }}>
                  {s.parsed.segments.length === 0 ? (
                    <span className="text-gray-400">{t('games.chooseWord.sentenceFallback', { index: idx + 1 })}</span>
                  ) : (
                    s.parsed.segments.map((seg, i) => {
                      if (seg.type === 'text') {
                        return (
                          <span key={i} style={{ whiteSpace: 'pre-wrap' }}>
                            {seg.text}
                          </span>
                        );
                      }
                      return (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1"
                          style={{ margin: '0 2px' }}
                        >
                          {renderOption(s, seg, 0)}
                          <span className="text-gray-400" style={{ fontSize: `${fontSize}px` }}>/</span>
                          {renderOption(s, seg, 1)}
                        </span>
                      );
                    })
                  )}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {!isEditor && (
        <div
          className={`flex items-center justify-end gap-2 ${
            hideChrome || isActionBarTransparent ? '' : 'border-t border-gray-100'
          }`}
          style={{
            backgroundColor: hideChrome ? 'transparent' : actionBarColor,
            padding: '8px 12px',
          }}
        >
          {!showResult && !hideCheckButton && (
            <button
              onClick={handleCheck}
              disabled={!allFilled}
              className="px-4 py-1.5 text-sm font-medium rounded-lg transition text-white disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
              style={{ backgroundColor: allFilled ? buttonColor : undefined }}
            >
              {t('games.common.check')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const gradeChooseWord = (element, answers) => {
  const sentences = element?.content?.sentences || [];
  const perChoiceResults = {};
  let total = 0;
  let correct = 0;
  sentences.forEach((s) => {
    const { choiceCount } = parseSentence(s.text || '');
    const given = answers?.[s.id] || [];
    const correctArr = s.correctIndices || [];
    const results = [];
    for (let i = 0; i < choiceCount; i += 1) {
      const isRight = given[i] === correctArr[i];
      results.push(isRight);
      total += 1;
      if (isRight) correct += 1;
    }
    perChoiceResults[s.id] = results;
  });
  const isCorrect = total > 0 && correct === total;
  return { perChoiceResults, isCorrect };
};

export default ChooseWordElement;
